import { useState, useEffect, useMemo } from "react";
import { T } from "@/components/T";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { useLivePanchang } from "@/lib/usePanchang";
import { getSavedLocation, nowInLocation } from "@/lib/cities";
import { taPhrase, fieldDefault, ovCell, phraseDictFor, makePanchangT } from "@/lib/panchangTamil";
import JourneyStrip from "@/components/JourneyStrip";
import { GlossaryProvider, GlossaryTerm } from "@/components/GlossaryTerm";
import PanchangExtras from "@/components/PanchangExtras";
import CivilTithiNote from "@/components/CivilTithiNote";
import PanchangShareImage from "@/components/PanchangShareImage";
import { PanchangPremiumModal } from "@/components/PanchangPremium";
import PremiumBanner from "@/components/PremiumBanner";
import LocationPicker from "@/components/LocationPicker";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import { pitruPakshaOn } from "@/lib/pitruPaksha";
import { ArrowRight, Clock, AlertTriangle, Star, CalendarDays, Info, Sparkles, Lock, Flame, Scroll, RotateCcw } from "lucide-react";
import { MoonIcon, SunIcon } from "@/components/CelestialIcons";
import { premiumStatus } from "@/lib/api";
import { toast } from "sonner";

// Window time "4:46 AM–5:37 AM" → two no-wrap halves so narrow cards break between
// start and end, never inside a time.
const spanParts = (v) => {
  if (!/\d/.test(String(v || ""))) return "—"; // no time today (e.g. Abhijit on Wednesday)
  const mm = String(v).match(/^(.+?)\s*[–-]\s*(.+)$/);
  return mm ? <><span className="whitespace-nowrap">{mm[1]}</span>{" "}<span className="whitespace-nowrap">– {mm[2]}</span></> : (v || "—");
};


// Shared entrance-animation variants (subtle staggered fade-in on scroll)
const gridStagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const riseItem = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.7, 0.2, 1] } },
};
// Result sections animate in on mount (not on scroll) so the timing cards are
// never left stuck at opacity:0 on mobile after a re-render / language switch.
const revealProps = {
  initial: "hidden",
  animate: "show",
};

// Is the selected time (HH:MM) inside a "HH:MM–HH:MM" window span? Supports past-midnight wrap.
function timeInSpan(span, hm) {
  if (!span || !hm) return false;
  const parts = String(span).split(/[–—-]/).map((s) => s.trim());
  if (parts.length !== 2) return false;
  const toMin = (t) => {
    const m = String(t).trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!m) return NaN;
    let h = +m[1]; const min = +m[2]; const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return h * 60 + min;
  };
  const [s, e] = parts.map(toMin);
  const t = toMin(hm);
  if ([s, e, t].some(Number.isNaN)) return false;
  return s <= e ? t >= s && t < e : t >= s || t < e;
}

// Presentational card — every string is already resolved to the active page
// language (English / Hindi / Tamil) by the parent, so it just renders.
function Item({ label, value, num, pada, padaLabel, range, next, nextLabel, devataLabel, devataText, devataTestid, fontClass }) {
  return (
    <motion.div variants={riseItem} className="pan-card pan-accent p-5">
      <p className="overline-label text-ink-3 pf-label"><GlossaryTerm>{label}</GlossaryTerm></p>
      <p className={`mt-2 ${fontClass} text-2xl text-ink deva-lh pf-value`}>
        {num != null && <span className="mr-1.5 text-gold dh-num">{num}.</span>}
        {value}
        {pada != null && <span className="ml-2 align-middle font-body text-sm text-ink-3 pf-meta">{padaLabel} {pada}</span>}
      </p>
      {devataText && (
        <p className="mt-1.5 text-[15px] text-ink-3 pf-devata" data-testid={devataTestid || undefined}>{devataLabel} · <span className={`text-ink-2 ${fontClass}`}>{devataText}</span></p>
      )}
      {range && (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-base font-medium text-ink-2 tabular-nums">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(199,80,0,0.07)] px-2.5 py-1 text-sm text-saffron pf-range"><Clock className="h-3.5 w-3.5 shrink-0" /> {range}</span>
        </div>
      )}
      {next && (
        <p className="mt-1.5 text-base text-ink-2 pf-next">{nextLabel}: {next}</p>
      )}
    </motion.div>
  );
}

const PA_LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
];

// Engine keys for the 8 proportional Karmakala divisions (titles stay visible to
// free users; only the computed times/significance are locked).
const KARMAKALA_ORDER = ["arunodaya", "pratah", "sangava", "madhyahna", "aparahna", "sayahna", "pradosha", "nishitha"];

// ── Locked placeholders for free (non-premium) users ──────────────────────────
// Real engine values are NEVER rendered for free users — only soft gold shimmer
// bars and a locked pill — so nothing leaks through "inspect element", while the
// section still reads as a premium, on-theme teaser.
const LockBar = ({ className = "w-24" }) => (
  <span className={`inline-block h-3 animate-pulse rounded-full bg-gradient-to-r from-[rgba(199,80,0,0.18)] via-[rgba(198,138,0,0.12)] to-[rgba(176,141,87,0.06)] ${className}`} aria-hidden="true" />
);
const LockTime = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)]/35 bg-[rgba(198,138,0,0.08)] px-2.5 py-1 text-sm font-medium tracking-[0.2em] text-gold/75 tabular-nums">
    <Lock className="h-3 w-3 shrink-0" /> •• : ••
  </span>
);
// Premium-styled container for a single locked row / card.
const lockRowCls = "flex items-center justify-between gap-3 rounded-xl border border-[var(--gold-muted)]/25 bg-[linear-gradient(135deg,rgba(199,80,0,0.05),rgba(176,141,87,0.035))] p-3.5";
const lockCardCls = "rounded-xl border border-[var(--gold-muted)]/25 bg-[linear-gradient(135deg,rgba(199,80,0,0.05),rgba(176,141,87,0.035))] p-4";
function UnlockBtn({ label, testid }) {
  return (
    <Link to="/panchanga-premium" data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_26px_-14px_rgba(199,80,0,0.7)] transition-transform hover:scale-[1.02]">
      <Lock className="h-3.5 w-3.5" /> {label}
    </Link>
  );
}


// Shimmer skeleton shown while the parallel panchang + lab requests resolve,
// so the page reserves space instead of flashing an empty "…".
const SkelBox = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-[rgba(0,0,0,0.06)] ${className}`} />
);
function PanchangSkeleton() {
  return (
    <div data-testid="panchang-skeleton" className="space-y-10">
      <div className="flex flex-wrap items-center gap-3">
        <SkelBox className="h-7 w-64" />
        <SkelBox className="h-7 w-28 rounded-full" />
      </div>
      <SkelBox className="h-24 w-full max-w-sm" />
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => <SkelBox key={i} className="h-24" />)}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => <SkelBox key={i} className="h-28" />)}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => <SkelBox key={i} className="h-32" />)}
      </div>
    </div>
  );
}


export default function Panchanga({ city = null }) {
  const { lang } = useLang();
  const { panchang_notes = [], panchang_sections = [], panchang_tamil = {}, panchang_lang_fonts = {} } = useSettings();
  const p2 = (x) => String(x).padStart(2, "0");

  // Page-scoped language toggle (does NOT change the site language). Persisted so
  // a browser refresh keeps the chosen language; defaults to the global language.
  const [paLang, setPaLang] = useState(() => {
    const saved = typeof localStorage !== "undefined" && localStorage.getItem("dh_panchang_lang");
    if (["en", "hi", "ta", "te"].includes(saved)) return saved;
    return ["hi", "ta", "te"].includes(lang) ? lang : "en";
  });
  const FC = { en: "font-serif-latin", hi: "font-deva", ta: "font-tamil", te: "font-telugu" };
  const BFC = { en: "", hi: "font-deva-body", ta: "font-tamil-body", te: "font-telugu-body" };
  const fontClass = FC[paLang] || FC.en;
  const bodyFontClass = BFC[paLang] || "";
  const isScript = paLang === "ta" || paLang === "te";
  const changePaLang = (code) => { setPaLang(code); try { localStorage.setItem("dh_panchang_lang", code); } catch (_) {} };

  // Admin-selectable per-script fonts (applied only within this page).
  const lf = panchang_lang_fonts || {};
  const scopeFontCss = [
    lf.ta && `[data-pf-lang-scope] .font-tamil,[data-pf-lang-scope] .font-tamil-body{font-family:'${lf.ta}','Noto Serif Tamil',serif !important;}`,
    lf.te && `[data-pf-lang-scope] .font-telugu,[data-pf-lang-scope] .font-telugu-body{font-family:'${lf.te}','Noto Serif Telugu',serif !important;}`,
    lf.hi && `[data-pf-lang-scope] .font-deva,[data-pf-lang-scope] .font-deva-body{font-family:'${lf.hi}','Noto Serif Devanagari',serif !important;}`,
  ].filter(Boolean).join("");

  // Per-field override layer: settings.panchang_tamil = { cat: { key: { en?, hi?, ta?, te? } } }
  // (a legacy plain string is treated as Tamil). Falls back to the live payload
  // (English/Devanagari) and the built-in per-language defaults.
  const i18n = panchang_tamil || {};
  const cell = (cat, key) => ovCell(i18n, cat, key || "");
  // Merged phrase dict for the active language (hi/ta/te) — built-in maps plus
  // any admin word-mappings. Used to translate free-form devata / "next" names.
  const langDict = useMemo(() => (paLang === "en" ? null : phraseDictFor(paLang, i18n)), [paLang, panchang_tamil]); // eslint-disable-line react-hooks/exhaustive-deps
  // Token translator honouring admin custom word-mappings in every language
  // (including English corrections) — used for free-form names like Samvatsara.
  const pt = useMemo(() => makePanchangT(paLang, i18n), [paLang, panchang_tamil]); // eslint-disable-line react-hooks/exhaustive-deps

  // UI label resolver (any active language).
  const U = (key) => {
    const o = cell("ui", key);
    if (paLang === "en") return o.en || fieldDefault("ui", key, "en") || key;
    return o[paLang] || fieldDefault("ui", key, paLang) || fieldDefault("ui", key, "en") || key;
  };
  // Term resolver for a {deva, en} field, using the English slot as the lookup key.
  const term = (cat, obj) => {
    if (!obj) return "";
    const key = obj.en || "";
    const o = cell(cat, key);
    if (paLang === "en") return o.en || obj.en || obj.deva || "";
    if (paLang === "hi") return o.hi || obj.deva || fieldDefault(cat, key, "hi") || obj.en || "";
    return o[paLang] || fieldDefault(cat, key, paLang) || obj.en || obj.deva || ""; // ta / te
  };
  // Free-form deity / "next" name (string or {deva,en}) → active language. Every
  // non-English language is translated token-by-token via langDict (which includes
  // admin word-mappings), so nothing leaks back to English/Devanagari.
  const nameStr = (v) => {
    if (!v) return "";
    if (typeof v === "object") {
      if (paLang === "en") return v.en || v.deva || "";
      if (paLang === "hi" && v.deva && v.deva !== v.en) return v.deva;
      return langDict ? taPhrase(v.en || v.deva || "", langDict) : (v.en || v.deva || "");
    }
    if (paLang === "en") return v;
    return langDict ? taPhrase(v, langDict) : v;
  };
  // Hora planet name + descriptor (keyed by the romanised-Sanskrit name).
  const horaName = (obj) => term("hora", obj);
  const horaNote = (item) => {
    const o = cell("hora_note", item.en);
    if (paLang === "en") return o.en || item.note || "";
    return o[paLang] || fieldDefault("hora_note", item.en, paLang) || item.note || "";
  };
  // Anchor "today"/"now" to the SELECTED LOCATION's timezone — not the browser's —
  // so a viewer in India inspecting London sees London's current date & time, and
  // every timing (hora, muhurta, choghadiya) is shown in that city's local time.
  const [locKey, setLocKey] = useState(0);
  const loc = useMemo(() => (
    city && city.lat != null
      ? { lat: city.lat, lon: city.lon, name: city.name, place: city.name, tz: city.tz, tz_id: city.tz_id }
      : (getSavedLocation() || { tz: 330, tz_id: "Asia/Kolkata" })
  ), [locKey, city]);
  const cityNow = () => nowInLocation(loc);
  const init = cityNow();
  const [live, setLive] = useState(true);
  const [date, setDate] = useState(`${init.year}-${p2(init.month)}-${p2(init.day)}`);
  const [time, setTime] = useState(`${p2(init.hour)}:${p2(init.min)}`);

  // While in "live" mode, keep the date/time controls synced to the selected
  // city's current wall-clock time (re-syncs on tick and when the city changes).
  useEffect(() => {
    if (!live) return;
    const sync = () => {
      const c = cityNow();
      setDate(`${c.year}-${p2(c.month)}-${p2(c.day)}`);
      setTime(`${p2(c.hour)}:${p2(c.min)}`);
    };
    sync();
    const id = setInterval(sync, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, locKey]);

  // Premium gating — Choghadiya and the scriptural almanac (festival windows,
  // karmakala, varjyam) show real values only for premium subscribers; everyone
  // else sees locked placeholders with NO real data rendered to the DOM. The
  // premium /lab almanac endpoint is not even fetched for free users.
  const [premium, setPremium] = useState(false);
  useEffect(() => { premiumStatus().then((s) => setPremium(!!s?.active)).catch(() => setPremium(false)); }, []);

  const override = time ? `${date}T${time}:00` : date;
  const { data, labData, loading, error, retry } = useLivePanchang(override, locKey, premium, city ? loc : null);
  const m = data?.muhurta || {};

  // Unified error → toast with a Retry action (re-fires both parallel requests).
  useEffect(() => {
    if (error) toast.error(U("load_error"), { description: error, action: { label: U("retry"), onClick: retry } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  // Engine unreachable → we are showing the last saved engine result (Rule 46).
  useEffect(() => {
    if (data?.source === "cache" && data.cached_at) {
      const when = new Date(data.cached_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
      toast.info(U("load_error"), { description: `Showing the last saved Panchangam (${when}).`, action: { label: U("retry"), onClick: retry } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.source, data?.cached_at]);

  // Karmakala window label (keyed by stable engine keys: pratah, madhyahna, …).
  const labKarma = (winKey) => {
    const o = cell("karmakala", winKey);
    if (paLang === "en") return o.en || fieldDefault("karmakala", winKey, "en") || winKey;
    return o[paLang] || fieldDefault("karmakala", winKey, paLang) || fieldDefault("karmakala", winKey, "en") || winKey;
  };

  const specialNotes = panchang_notes.filter((nt) => nt.date === date);
  const ppToday = pitruPakshaOn(date);
  const paLocale = paLang === "ta" ? "ta-IN" : paLang === "te" ? "te-IN" : paLang === "hi" ? "hi-IN" : "en-US";
  // Localised transition range "01:02 PM (Fri) – 01:02 PM (Sat)" — weekday and
  // AM/PM rendered in the active language (never hardcoded English) from the raw
  // timestamps, anchored to the selected city's timezone.
  const fmtRange = (b) => {
    if (!b || b[0] == null || b[1] == null) return "";
    const tz = data?.tz;
    const tOpt = { hour: "2-digit", minute: "2-digit", hour12: true, ...(tz ? { timeZone: tz } : {}) };
    const wOpt = { weekday: "short", ...(tz ? { timeZone: tz } : {}) };
    const one = (ts) => {
      const d = new Date(ts * 1000);
      return `${d.toLocaleTimeString(paLocale, tOpt)} (${d.toLocaleDateString(paLocale, wOpt)})`;
    };
    return `${one(b[0])} - ${one(b[1])}`;
  };
  const rng = (anga) => (anga && anga.b ? fmtRange(anga.b) : (anga && anga.range) || "");
  // Lab-slot time helpers — the almanac returns raw unix start/end, so format a
  // compact "HH:MM AM – HH:MM PM" in the active language & selected timezone.
  const labTz = data?.tz || labData?.tz;
  const fmtClock = (ts) => {
    if (ts == null) return "";
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString(paLocale, { hour: "2-digit", minute: "2-digit", hour12: true, ...(labTz ? { timeZone: labTz } : {}) });
  };
  const fmtSpanUnix = (a, b) => (a != null && b != null ? `${fmtClock(a)} – ${fmtClock(b)}` : "");
  const dateObj = new Date(date + "T00:00:00");
  const weekdayName = dateObj.toLocaleDateString(paLocale, { weekday: "long" });
  const dateStr = dateObj.toLocaleDateString(paLocale, { day: "numeric", month: "long", year: "numeric" });
  // Content notes (special dates, pitru copy, custom sections) are authored in
  // en/hi only; Tamil falls back to English.
  const pickC = (hi, en) => (paLang === "hi" ? (hi || en) : (en || hi));

  return (
    <GlossaryProvider source="panchang">
    <div data-pf-lang-scope>
      {scopeFontCss && <style dangerouslySetInnerHTML={{ __html: scopeFontCss }} />}
      <section className="relative z-20 border-b border-line bg-[var(--surface)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--gold-muted)] to-transparent opacity-60" />
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative z-[2] mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-20"
        >
          {/* Page-scoped language toggle — its OWN row so it never shifts with the
              title width when the language changes. Right-aligned on desktop. */}
          <div className="mb-6 flex justify-start md:justify-end">
            <div className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-line bg-parchment p-1" data-testid="panchang-lang-toggle">
              {PA_LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`panchang-lang-${l.code}`}
                  onClick={() => changePaLang(l.code)}
                  aria-pressed={paLang === l.code}
                  className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${paLang === l.code ? "bg-[var(--saffron)] text-white" : "text-ink-2 hover:text-saffron"} ${l.code === "ta" ? "font-tamil-body" : l.code === "te" ? "font-telugu-body" : l.code === "hi" ? "font-deva-body" : ""}`}
                >{l.label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline-label mb-3 pf-hero-overline">{city ? `${U("daily_almanac")} · ${city.region || city.country || ""}`.replace(/ · $/, "") : U("daily_almanac")}</p>
            <h1 className={`${fontClass} text-5xl text-ink deva-lh md:text-6xl pf-hero-title`}>{city ? `${U("panchanga")} · ${city.name}` : U("panchanga")}</h1>
          </div>
          <p className={`mt-4 max-w-2xl leading-relaxed text-ink-2 pf-hero-subtitle ${bodyFontClass}`}>
            {U("hero_subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap items-end gap-x-5 gap-y-4">
            <div className="flex flex-col gap-1.5">
              <span className="overline-label text-ink-3 pf-control-label">{U("date")}</span>
              <div className="pan-control flex items-center gap-2 px-3">
                <CalendarDays className="h-4 w-4 shrink-0 text-saffron" />
                <input
                  data-testid="panchang-date"
                  type="date"
                  value={date}
                  onChange={(e) => { setLive(false); setDate(e.target.value); }}
                  onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) { /* blocked in cross-origin preview iframe */ } }}
                  className="w-[9.5rem] bg-transparent py-2.5 text-ink tabular-nums [color-scheme:light] dark:[color-scheme:dark] focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="overline-label text-ink-3 pf-control-label">{U("time")}</span>
              <div className="pan-control flex items-center gap-2 px-3">
                <Clock className="h-4 w-4 shrink-0 text-saffron" />
                <input
                  data-testid="panchang-time"
                  type="time"
                  lang="en-GB"
                  value={time}
                  onChange={(e) => { setLive(false); setTime(e.target.value); }}
                  onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) { /* blocked in cross-origin preview iframe */ } }}
                  className="w-[6.5rem] bg-transparent py-2.5 text-center text-ink tabular-nums [color-scheme:light] dark:[color-scheme:dark] focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="overline-label text-ink-3 invisible" aria-hidden="true">{U("now")}</span>
              <button
                data-testid="panchang-now"
                onClick={() => { setLive(true); const c = cityNow(); setDate(`${c.year}-${p2(c.month)}-${p2(c.day)}`); setTime(`${p2(c.hour)}:${p2(c.min)}`); }}
                className="pan-control px-5 py-2.5 text-sm font-medium text-saffron hover:text-saffron-dark"
              >{U("now")}</button>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="overline-label text-ink-3 pf-control-label">{U("location")}</span>
              {city ? (
                <div className="pan-control flex items-center gap-2 px-4 py-2.5" data-testid="city-location-fixed">
                  <Info className="h-4 w-4 shrink-0 text-saffron" />
                  <span className="text-ink">{city.name}{city.region ? `, ${city.region}` : ""}{city.country ? `, ${city.country}` : ""}</span>
                </div>
              ) : (
                <LocationPicker onChange={() => setLocKey((k) => k + 1)} />
              )}
            </div>
          </div>
          {specialNotes.length > 0 && (
            <div className="mt-6 space-y-3" data-testid="panchang-special-notes">
              {specialNotes.map((sn, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg border-l-2 border-saffron bg-[rgba(199,80,0,0.06)] px-4 py-3 shadow-[0_10px_28px_-20px_rgba(199,80,0,0.5)]" data-testid={`panchang-special-note-${i}`}>
                  {sn.image
                    ? <img src={sn.image} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                    : <Star className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />}
                  <div>
                    <p className="font-serif-latin text-lg text-ink pf-special-title">
                      {pickC(sn.hi, sn.en) || sn.en || sn.hi}
                      {sn.time && <span className="ml-2 text-sm tabular-nums text-saffron">· {sn.time}</span>}
                    </p>
                    {(pickC(sn.note_hi, sn.note_en) || sn.note) && <p className="mt-0.5 text-sm text-ink-2 pf-special-note">{pickC(sn.note_hi, sn.note_en) || sn.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-16">
        {loading && !data ? (
          <PanchangSkeleton />
        ) : !data ? (
          <div className="py-16 text-center" data-testid="panchang-error">
            <p className="text-ink-2">{pickC("इस तिथि एवं स्थान का पंचांग अभी उपलब्ध नहीं है — कृपया दूसरी तिथि आज़माएँ।", "Panchangam for this date & place is unavailable right now — please try another date.")}</p>
            {error && <p className="mt-1 text-sm text-ink-3">{error}</p>}
            <button onClick={retry} data-testid="panchang-retry" className="mt-5 inline-flex items-center gap-2 rounded-full border border-saffron bg-[rgba(199,80,0,0.06)] px-5 py-2.5 text-sm font-medium text-saffron transition-colors hover:bg-saffron hover:text-white">
              <RotateCcw className="h-4 w-4" /> {U("retry")}
            </button>
          </div>
        ) : (
          <div data-testid="panchang-result">
            {data.polar && (
              <div data-testid="panchang-polar-notice" className="mb-6 flex items-start gap-3 rounded-lg border-l-2 border-gold bg-[rgba(176,141,87,0.09)] px-4 py-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <p className={`text-sm leading-relaxed text-ink-2 ${bodyFontClass}`}>
                  {U("polar_notice")}
                </p>
              </div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
            >
              <p className={`text-2xl text-ink pf-date-heading ${fontClass}`}>{weekdayName}, {dateStr}</p>
              <span className={`inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)]/60 bg-[rgba(198,138,0,0.08)] px-3 py-1 text-sm text-gold ${bodyFontClass}`}>
                <MoonIcon className="h-3.5 w-3.5" /> {term("paksha", data.paksha)} {U("paksha")}
              </span>
              <PanchangShareImage data={data} className="ml-auto" />
              <Link to="/panchanga-premium" data-testid="panchang-premium-cta" className="inline-flex items-center gap-1.5 rounded-full border border-saffron bg-[rgba(199,80,0,0.06)] px-4 py-2 text-sm font-medium text-saffron transition-colors hover:bg-saffron hover:text-white">
                <Sparkles className="h-4 w-4" /> {pickC("पंचांग प्रीमियम", "Panchangam Premium")}
              </Link>
            </motion.div>

            {/* Pitru Paksha awareness — shown when the selected date lies within
                the Pitru Paksha fortnight. Tithi mapping follows the standard
                Dharmartha Jyotish Engine reckoning; see /pitru-paksha for the full guide. */}
            {ppToday.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="mt-6 overflow-hidden rounded-xl border border-[var(--saffron)]/35 bg-[linear-gradient(135deg,rgba(199,80,0,0.06),rgba(184,134,11,0.06))]"
                data-testid="panchang-pitru-paksha"
              >
                <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 overline-label text-saffron"><Star className="h-3.5 w-3.5" /> {U("pitru_paksha")}</p>
                    <p className={`mt-1.5 text-xl text-ink deva-lh ${paLang === "hi" ? "font-deva" : "font-serif-latin"}`}>
                      {ppToday.map((e, i) => (
                        <span key={i}>{i > 0 && <span className="text-ink-3"> · </span>}{pickC(e.tithi_hi, e.tithi_en)}</span>
                      ))}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-ink-2">{pickC(ppToday[0].note_hi, ppToday[0].note_en)}</p>
                    <p className="mt-2 text-xs italic text-ink-3">{pickC("आज इस तिथि पर देहावसान हुए पूर्वजों का श्राद्ध एवं तर्पण किया जाता है। · स्रोत: धर्मार्थ ज्योतिष इंजन", "Shraddha & Tarpana are offered today for ancestors who departed on this tithi. · Source: Dharmartha Jyotish Engine")}</p>
                  </div>
                  <Link to="/pitru-paksha" data-testid="panchang-pitru-link" className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-[var(--saffron)] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] sm:self-center">
                    {U("open_guide")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            )}

            {/* 1. Current planetary hour (Hora) */}
            {data.hora && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.05 }}
                className="pan-glass mt-6 p-5" data-testid="panchang-hora"
              >
                <p className="flex items-center gap-1.5 overline-label text-gold pf-hora-meta"><Clock className="h-3.5 w-3.5" /> {U("current_hora")}</p>
                <p className={`mt-1.5 text-2xl text-ink pf-hora-title ${fontClass}`}>{horaName(data.hora)}</p>
                <p className="mt-1 text-xl font-semibold text-ink tabular-nums pf-hora-meta">{data.hora.span}</p>
                <p className="mt-0.5 text-sm text-ink-3 pf-hora-meta">{data.hora.part === "day" ? U("day") : U("night")}</p>
              </motion.div>
            )}

            {/* Auspicious & inauspicious windows — fixed order:
                Brahma Muhurta, Abhijit Muhurta, Rahu Kalam, Yama Gandam, Gulika Kalam */}
            <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
              <h2 className={`text-2xl text-ink pf-section-heading ${fontClass}`}>{U("windows_heading")}</h2>
              <PanchangShareImage data={data} variant="muhurta" />
            </div>
            <motion.div {...revealProps} variants={gridStagger} className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4" data-testid="panchang-windows">
              {[
                [U("brahma"), m.brahma, false, U("brahma_tip")],
                [U("abhijit"), m.abhijit, false],
                [U("rahu"), m.rahu_kalam, true],
                [U("yama"), m.yamaganda, true],
                [U("gulika"), m.gulika_kalam, true],
              ].map(([label, value, warn, tip]) => {
                const active = timeInSpan(value, time);
                const tone = warn ? "bad" : "good";
                return (
                  <motion.div key={label} variants={riseItem} data-testid={`panchang-window-${label}`} title={tip || undefined}
                    className="pwin" data-on={active ? "true" : "false"} data-win={tone}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex items-center gap-1.5 overline-label text-ink-2 pf-window-label">{label}{tip && <Info className="h-3.5 w-3.5 text-ink-3/70" aria-label={tip} />}</span>
                      {active && <span data-testid="window-now-badge" className="pwin-now shrink-0">{U("now_badge")}</span>}
                    </div>
                    <span className={`pwin-time text-ink pf-window-time ${fontClass}`}>{spanParts(value)}</span>
                    {/\d/.test(String(value || "")) && <span className={`pwin-q ${bodyFontClass}`} data-tone={tone}>{warn ? pickC("त्याज्य", "Avoid") : pickC("शुभ", "Auspicious")}</span>}
                  </motion.div>
                );
              })}
            </motion.div>

            <Divider className="my-12" />

            {/* 6–10. Vara, Tithi, Nakshatra (each with devata), Yoga, Karana */}
            <motion.div {...revealProps} variants={gridStagger} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Item label={U("vara")} value={term("vara", data.vara)} devataLabel={U("devata")} devataText={nameStr(data.vara.devata)} devataTestid="vara-devata-inline" range={rng(data.vara)} next={nameStr(data.vara.next)} nextLabel={U("next")} fontClass={fontClass} />
              <Item label={U("tithi")} value={`${term("paksha", data.paksha)} ${term("tithi", data.tithi)}`} num={data.tithi.number} range={rng(data.tithi)} next={nameStr(data.tithi.next)} nextLabel={U("next")} devataLabel={U("devata")} devataText={nameStr(data.tithi.devata)} fontClass={fontClass} />
              <Item label={U("nakshatra")} value={term("nakshatra", data.nakshatra)} pada={data.nakshatra.pada} padaLabel={U("pada")} range={rng(data.nakshatra)} next={nameStr(data.nakshatra.next)} nextLabel={U("next")} devataLabel={U("devata")} devataText={nameStr(data.nakshatra.devata)} fontClass={fontClass} />
              <Item label={U("yoga")} value={term("yoga", data.yoga)} range={rng(data.yoga)} next={nameStr(data.yoga.next)} nextLabel={U("next")} fontClass={fontClass} />
              <Item label={U("karana")} value={term("karana", data.karana)} range={rng(data.karana)} next={nameStr(data.karana.next)} nextLabel={U("next")} fontClass={fontClass} />
              <motion.div variants={riseItem} className="pan-card pan-accent p-5" data-testid="panchang-lunar-month">
                <p className="flex items-center gap-1.5 overline-label text-ink-3 pf-label"><CalendarDays className="h-3.5 w-3.5 text-gold" /> {U("lunar_month")}</p>
                <p className={`mt-2 text-2xl text-ink deva-lh pf-value ${fontClass}`}>{term("masa", data.amanta) || "—"} <span className="text-base text-ink-3 pf-meta">({U("amanta")})</span></p>
                <p className={`mt-1 text-2xl text-ink deva-lh pf-value ${fontClass}`}>{term("masa", data.purnimanta) || "—"} <span className="text-base text-ink-3 pf-meta">({U("purnimanta")})</span></p>
              </motion.div>
            </motion.div>

            {/* 11–12. Moon (rise / set / rashi) and Sun (rise / set / rashi) */}
            <motion.div {...revealProps} variants={gridStagger} className="mt-4 grid gap-4 sm:grid-cols-2">
              <motion.div variants={riseItem} className="pan-card p-5 !border-[var(--gold-muted)]/40 !bg-[linear-gradient(135deg,rgba(176,141,87,0.06),rgba(198,138,0,0.04))]" data-testid="panchang-moon">
                <p className="flex items-center gap-1.5 overline-label text-ink-3 pf-label"><MoonIcon className="h-4 w-4 text-gold" /> {U("moon")}</p>
                <p className="mt-2"><span className={`text-2xl text-ink deva-lh pf-value ${fontClass}`}>{term("rashi", data.moon_rashi)}</span> <span className="text-ink-2 pf-meta">{U("rashi")}</span></p>
                <div className="mt-3 grid grid-cols-2 gap-2 tabular-nums">
                  {data.moon_note ? (
                    <p data-testid="panchang-moon-status" className="col-span-2 font-serif-latin text-lg text-ink">{data.moon_note}</p>
                  ) : (<>
                  <div><p className="overline-label text-ink-3 pf-meta">{U("moonrise")}</p><p className="font-serif-latin text-lg text-ink pf-window-time">{data.moonrise || "—"}</p></div>
                  <div><p className="overline-label text-ink-3 pf-meta">{U("moonset")}</p><p className="font-serif-latin text-lg text-ink pf-window-time">{data.moonset || "—"}</p></div>
                  </>)}
                </div>
                {data.moon_length && <p className="mt-2 text-sm text-ink-2 pf-meta">{U("moon_length")} {data.moon_length}</p>}
              </motion.div>
              <motion.div variants={riseItem} className="pan-card p-5 !border-[var(--saffron)]/25 !bg-[linear-gradient(135deg,rgba(199,80,0,0.06),rgba(198,138,0,0.045))]" data-testid="panchang-sun">
                <p className="flex items-center gap-1.5 overline-label text-ink-3 pf-label"><SunIcon className="h-4 w-4 text-saffron" /> {U("sun")}</p>
                <p className="mt-2"><span className={`text-2xl text-ink deva-lh pf-value ${fontClass}`}>{term("rashi", data.sun_rashi)}</span> <span className="text-ink-2 pf-meta">{U("rashi")}</span></p>
                <div className="mt-3 grid grid-cols-2 gap-2 tabular-nums">
                  {data.sun_note ? (
                    <p data-testid="panchang-sun-status" className="col-span-2 font-serif-latin text-lg text-ink">{data.sun_note}</p>
                  ) : (<>
                  <div><p className="overline-label text-ink-3 pf-meta">{U("sunrise")}</p><p className="font-serif-latin text-lg text-ink pf-window-time">{data.sunrise || "—"}</p></div>
                  <div><p className="overline-label text-ink-3 pf-meta">{U("sunset")}</p><p className="font-serif-latin text-lg text-ink pf-window-time">{data.sunset || "—"}</p></div>
                  </>)}
                </div>
                {data.day_length && <p className="mt-2 text-sm text-ink-2">{U("day_length")} {data.day_length}</p>}
              </motion.div>
            </motion.div>

            {/* 13–14. Ritu, Samvat */}
            <motion.div {...revealProps} variants={gridStagger} className="mt-4 grid gap-4 sm:grid-cols-2">
              <Item label={U("ritu")} value={term("ritu", data.ritu)} fontClass={fontClass} />
              <motion.div variants={riseItem} className="pan-card pan-accent p-5" data-testid="panchang-samvat">
                <p className="overline-label text-ink-3 pf-label">{U("samvat")}</p>
                <p className={`mt-2 ${fontClass} text-2xl text-ink deva-lh pf-value`}>{U("vikram")}: {data?.vikram_samvat ?? ''}</p>
                <p className={`mt-2 ${fontClass} text-2xl text-ink deva-lh pf-value`}>{U("shaka")}: {data?.shaka_samvat ?? ''}</p>
                <p className={`mt-2 ${fontClass} text-2xl text-ink deva-lh pf-value`}>{pt("Sauramana")}: {pt(String(data?.sauramana_samvat ?? data?.samvat?.sauramana ?? '').replace(/^\s*SS\s*/i, '').trim())}</p>
              </motion.div>
            </motion.div>

            <Divider className="my-12" />

            {Array.isArray(data.hora_timeline) && data.hora_timeline.length > 0 && (
              <div data-testid="hora-timeline">
                <h2 className={`text-2xl text-ink ${fontClass}`}>{U("hora_timeline")}</h2>
                <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("hora_timeline_sub")}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-ink-3">
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1"><span className="h-2.5 w-2.5 rounded-full bg-green-700" /> {U("q_strong")}</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1"><span className="h-2.5 w-2.5 rounded-full bg-green-400" /> {U("q_favourable")}</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1"><span className="h-2.5 w-2.5 rounded-full bg-yellow-400" /> {U("q_alert")}</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1"><span className="h-2.5 w-2.5 rounded-full bg-orange-600" /> {U("q_caution")}</span>
                </div>
                {["day", "night"].map((part) => (
                  <div key={part} className="mt-6">
                    <p className={`overline-label mb-3 text-ink-3 ${bodyFontClass}`}>{part === "day" ? U("day_part") : U("night_part")}</p>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {data.hora_timeline.filter((h) => h.part === part).map((h, i) => {
                        const HORA_DOT = { strong: "bg-green-700", favourable: "bg-green-400", favorable: "bg-green-400", alert: "bg-yellow-400", caution: "bg-orange-600", good: "bg-green-400", neutral: "bg-yellow-400" };
                        const dot = HORA_DOT[h.quality] || "bg-[var(--gold)]";
                        return (
                          <div key={i} data-testid={h.current ? "hora-current" : undefined}
                            className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${h.current ? "border-saffron bg-[rgba(199,80,0,0.06)] shadow-[0_10px_26px_-20px_rgba(199,80,0,0.6)]" : "border-line bg-surface hover:border-[var(--gold-muted)]/70"}`}>
                            <div className="flex items-center gap-2.5">
                              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
                              <div>
                                <p className="leading-tight"><span className={`text-lg text-ink ${fontClass}`}>{horaName(h)}</span>{h.current && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-saffron">{U("now_badge")}</span>}</p>
                                <p className={`text-sm leading-tight text-ink-2 ${bodyFontClass}`}>{horaNote(h)}</p>
                              </div>
                            </div>
                            <span className="shrink-0 font-serif-latin text-lg font-semibold text-ink tabular-nums">{h.span}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {premium ? (() => {
              // Prefer the richer 16-slot lab choghadiya; fall back to the core
              // panchang's 8+8 slots. Premium subscribers only.
              const labSlots = Array.isArray(labData?.slots) ? labData.slots : null;
              const byPart = labSlots ? { day: labSlots.filter((s) => s.period === "day"), night: labSlots.filter((s) => s.period === "night") } : null;
              const hasLab = byPart && (byPart.day.length > 0 || byPart.night.length > 0);
              const hasLegacy = data.choghadiya && data.choghadiya.day?.length > 0;
              if (!hasLab && !hasLegacy) return null;
              const dotFor = (q) => (q === "good" ? "bg-green-600" : q === "neutral" ? "bg-[var(--gold)]" : "bg-[var(--saffron-dark)]");
              return (
                <>
                  <Divider className="my-12" />
                  <div data-testid="choghadiya">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className={`text-2xl text-ink pf-section-heading ${fontClass}`}>{U("choghadiya")}</h2>
                      <PanchangShareImage data={data} variant="choghadiya" />
                    </div>
                    <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("choghadiya_sub")}</p>
                    <div className="mt-6 grid gap-8 lg:grid-cols-2">
                      {["day", "night"].map((part) => {
                        const rows = hasLab
                          ? byPart[part].map((s) => ({ name: s.name, nature: s.quality, span: fmtSpanUnix(s.start, s.end), current: s.isCurrent, note: "" }))
                          : (data.choghadiya[part] || []).map((c) => ({ name: paLang === "hi" ? (c.deva || c.name) : (c.name || c.deva), nature: c.nature, span: c.span, current: c.current, note: c.note }));
                        return (
                          <div key={part}>
                            <p className={`overline-label mb-3 text-ink-3 pf-window-label ${bodyFontClass}`}>{part === "day" ? U("day_choghadiya") : U("night_choghadiya")}</p>
                            <div className="space-y-2">
                              {rows.map((c, i) => (
                                <div key={i} data-testid={c.current ? `chogh-current-${part}` : `chogh-${part}-${i}`}
                                  className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${c.current ? "border-saffron bg-[rgba(199,80,0,0.06)] shadow-[0_10px_26px_-20px_rgba(199,80,0,0.6)]" : "border-line bg-surface hover:border-[var(--gold-muted)]/70"}`}>
                                  <div className="flex items-center gap-2.5">
                                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotFor(c.nature)}`} />
                                    <div>
                                      <p className="leading-tight"><span className={`text-ink ${fontClass}`}>{nameStr(c.name)}</span>{c.current && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-saffron">{U("now_badge")}</span>}</p>
                                      {c.note && <p className="text-sm leading-tight text-ink-2">{c.note}</p>}
                                    </div>
                                  </div>
                                  <span className="shrink-0 font-serif-latin text-sm text-ink-2 tabular-nums">{c.span}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })() : (
              <>
                <Divider className="my-12" />
                <div data-testid="choghadiya">
                  <h2 className={`text-2xl text-ink pf-section-heading ${fontClass}`}>{U("choghadiya")}</h2>
                  <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("choghadiya_sub")}</p>
                  <div className="mt-4"><UnlockBtn label={U("unlock_premium")} testid="choghadiya-unlock-cta" /></div>
                  <div className="mt-6 grid gap-8 lg:grid-cols-2">
                    {["day", "night"].map((part) => (
                      <div key={part}>
                        <p className={`overline-label mb-3 text-ink-3 pf-window-label ${bodyFontClass}`}>{part === "day" ? U("day_choghadiya") : U("night_choghadiya")}</p>
                        <div className="space-y-2">
                          {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} data-testid={`chogh-locked-${part}-${i}`} className={lockRowCls}>
                              <div className="flex items-center gap-2.5">
                                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-gold/40" />
                                <LockBar className="w-20" />
                              </div>
                              <LockTime />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Scriptural Almanac (premium) — festival windows, karmakala, varjyam,
                siddhi yogas. Values show for premium subscribers; others get a
                locked teaser. All strings sourced from the same public engine. */}
            <Divider className="my-12" />
            <div data-testid="panchang-premium-almanac">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className={`flex items-center gap-2 text-2xl text-ink pf-section-heading ${fontClass}`}>
                  <Sparkles className="h-5 w-5 text-saffron" /> {U("premium_almanac")}
                </h2>
              </div>
              <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("premium_almanac_sub")}</p>

              {premium ? (
                <div className="mt-6 space-y-10" data-testid="almanac-premium-content">
                  {/* Festival & Puja Hub */}
                  {(() => {
                    // Real observances are objects; on days without one the engine sends a plain sentence.
                    const pujas = Array.isArray(labData?.festivals?.special_pujas_today) ? labData.festivals.special_pujas_today.filter((x) => x && typeof x === "object" && (x.festival || x.name)) : [];
                    return (
                      <div data-testid="almanac-festivals">
                        <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><Flame className="h-4 w-4 text-saffron" /> {U("festival_hub")}</h3>
                        {pujas.length === 0 ? (
                          <p className={`mt-2 text-sm text-ink-3 ${bodyFontClass}`}>{U("no_pujas")}</p>
                        ) : (
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            {pujas.map((f, i) => (
                              <div key={i} data-testid={`almanac-festival-${i}`} className="pan-card pan-accent p-4">
                                <p className={`text-lg text-ink deva-lh ${fontClass}`}>{nameStr(f.festival || f.name)}</p>
                                {f.muhurta_window && <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[rgba(199,80,0,0.07)] px-2.5 py-1 text-sm text-saffron tabular-nums"><Clock className="h-3.5 w-3.5" /> {f.muhurta_window}</p>}
                                {f.significance && <p className={`mt-2 text-sm leading-relaxed text-ink-2 ${bodyFontClass}`}>{f.significance}</p>}
                                {f.authority && <p className="mt-1.5 text-xs italic text-ink-3">{f.authority}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Scriptural Karmakala Windows */}
                  {(() => {
                    const kw = labData?.festivals?.karmakala_windows || {};
                    const order = ["arunodaya", "pratah", "sangava", "madhyahna", "aparahna", "sayahna", "pradosha", "nishitha"];
                    const keys = Object.keys(kw).sort((a, b) => ((order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99)));
                    if (keys.length === 0) return null;
                    return (
                      <div data-testid="almanac-karmakala">
                        <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><Scroll className="h-4 w-4 text-gold" /> {U("karmakala")}</h3>
                        <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("karmakala_sub")}</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {keys.map((k) => {
                            const w = kw[k] || {};
                            return (
                              <div key={k} data-testid={`almanac-karmakala-${k}`} className="pan-card p-4">
                                <p className={`text-lg text-ink deva-lh ${fontClass}`}>{labKarma(k)}</p>
                                <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[rgba(176,141,87,0.1)] px-2.5 py-1 text-sm text-gold tabular-nums"><Clock className="h-3.5 w-3.5" /> {w.start_str} – {w.end_str}</p>
                                {w.significance && <p className={`mt-2 text-sm leading-relaxed text-ink-2 ${bodyFontClass}`}>{w.significance}</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Varjyam (Tyajya Kala) */}
                  {(() => {
                    const all = Array.isArray(labData?.varjyam_all) && labData.varjyam_all.length ? labData.varjyam_all : (labData?.varjyam ? [labData.varjyam] : []);
                    if (all.length === 0) return null;
                    return (
                      <div data-testid="almanac-varjyam">
                        <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><AlertTriangle className="h-4 w-4 text-[var(--saffron-dark)]" /> {U("varjyam")}</h3>
                        <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("varjyam_sub")}</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {all.map((v, i) => (
                            <div key={i} data-testid={`almanac-varjyam-${i}`} className="rounded-xl border-l-2 border-[var(--saffron-dark)] bg-[rgba(199,80,0,0.05)] p-4">
                              <p className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(199,80,0,0.1)] px-2.5 py-1 text-sm font-medium text-saffron tabular-nums"><Clock className="h-3.5 w-3.5" /> {v.start_str} – {v.end_str}</p>
                              <p className={`mt-2 text-sm text-ink-2 ${bodyFontClass}`}>{nameStr(v.nakshatra)} · {v.nature}{v.duration_minutes ? ` · ${Math.round(v.duration_minutes)} min` : ""}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Siddhi Yogas */}
                  {(() => {
                    const sy = Array.isArray(labData?.siddhi_yogas) ? labData.siddhi_yogas : [];
                    if (sy.length === 0) return null;
                    return (
                      <div data-testid="almanac-siddhi">
                        <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><Star className="h-4 w-4 text-gold" /> {U("siddhi_yoga")}</h3>
                        <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("siddhi_sub")}</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {sy.map((s, i) => (
                            <div key={i} data-testid={`almanac-siddhi-${i}`} className="pan-card pan-accent p-4">
                              <p className={`text-lg text-ink deva-lh ${fontClass}`}>{nameStr(s.name)}</p>
                              {s.active_window && <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[rgba(176,141,87,0.1)] px-2.5 py-1 text-sm text-gold tabular-nums"><Clock className="h-3.5 w-3.5" /> {s.active_window}</p>}
                              {s.type && <p className="mt-1.5 text-xs uppercase tracking-wide text-gold/80">{s.type}</p>}
                              {s.significance && <p className={`mt-2 text-sm leading-relaxed text-ink-2 ${bodyFontClass}`}>{s.significance}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="mt-6 space-y-10" data-testid="almanac-locked">
                  {/* Festival & Puja Windows — heading + description visible; values locked */}
                  <div data-testid="almanac-festivals-locked">
                    <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><Flame className="h-4 w-4 text-saffron" /> {U("festival_hub")}</h3>
                    <div className="mt-3"><UnlockBtn label={U("unlock_premium")} testid="almanac-festivals-unlock" /></div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} data-testid={`almanac-festival-locked-${i}`} className={lockCardCls}>
                          <LockBar className="w-36" />
                          <div className="mt-2.5"><LockTime /></div>
                          <div className="mt-2.5 space-y-1.5"><LockBar className="w-full" /><LockBar className="w-2/3" /></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Scriptural Karmakala Windows — tile titles visible; times/notes locked */}
                  <div data-testid="almanac-karmakala-locked">
                    <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><Scroll className="h-4 w-4 text-gold" /> {U("karmakala")}</h3>
                    <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("karmakala_sub")}</p>
                    <div className="mt-3"><UnlockBtn label={U("unlock_premium")} testid="almanac-karmakala-unlock" /></div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {KARMAKALA_ORDER.map((k) => (
                        <div key={k} data-testid={`almanac-karmakala-locked-${k}`} className={lockCardCls}>
                          <p className={`text-lg text-ink deva-lh ${fontClass}`}>{labKarma(k)}</p>
                          <div className="mt-2"><LockTime /></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Varjyam (Tyajya Kala) — heading + description visible; values locked */}
                  <div data-testid="almanac-varjyam-locked">
                    <h3 className={`flex items-center gap-2 text-xl text-ink deva-lh ${fontClass}`}><AlertTriangle className="h-4 w-4 text-[var(--saffron-dark)]" /> {U("varjyam")}</h3>
                    <p className={`mt-1 text-sm text-ink-3 ${bodyFontClass}`}>{U("varjyam_sub")}</p>
                    <div className="mt-3"><UnlockBtn label={U("unlock_premium")} testid="almanac-varjyam-unlock" /></div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} data-testid={`almanac-varjyam-locked-${i}`} className={`${lockCardCls} border-l-2 border-l-[var(--saffron-dark)]/40`}>
                          <LockTime />
                          <div className="mt-2.5"><LockBar className="w-40" /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Divider className="my-12" />

            <p className={`mt-8 text-xs italic leading-relaxed text-ink-3 ${bodyFontClass}`} data-testid="panchang-compute-note">{U("compute_note")} · {U("ayanamsa")} ≈ {data.ayanamsa}°</p>

            <div className="mt-12 flex flex-wrap items-center gap-4 border-t border-line pt-8">
              <p className="text-ink-2"><T>Need a personalised or regional Panchangam?</T></p>
              <Link to="/services/panchang" className="inline-flex items-center gap-2 border border-[var(--ink)] px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-[var(--surface)]">
                <T>Request a Panchangam reading</T> <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </section>

      {panchang_notes.length > 0 && (
        <section className="mx-auto max-w-5xl px-5 pb-4 md:px-8" data-testid="panchang-all-notes">
          <div className="border-t border-line pt-12">
            <h2 className={`flex items-center gap-2 text-2xl text-ink deva-lh ${fontClass}`}>
              <Star className="h-5 w-5 text-saffron" /> {U("marked_dates")}
            </h2>
            <div className="mt-6 divide-y divide-line border-y border-line">
              {[...panchang_notes].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((sn, i) => {
                const active = sn.date === date;
                return (
                  <button
                    key={i}
                    onClick={() => { setLive(false); setDate(sn.date); setTime(sn.time || ""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    data-testid={`panchang-note-row-${i}`}
                    className={`flex w-full items-start gap-4 py-4 text-left transition-colors hover:bg-parchment/60 ${active ? "bg-[rgba(199,80,0,0.05)]" : ""}`}
                  >
                    <span className={`mt-0.5 shrink-0 tabular-nums text-sm ${active ? "text-saffron" : "text-ink-3"}`}>
                      {sn.date ? new Date(sn.date + "T00:00:00").toLocaleDateString(paLocale, { day: "2-digit", month: "short", year: "numeric" }) : ""}
                      {sn.time && <span className="mt-0.5 block text-xs text-ink-3">{sn.time}</span>}
                    </span>
                    {sn.image && <img src={sn.image} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />}
                    <span className="flex-1">
                      <span className="font-serif-latin text-ink pf-special-title">{pickC(sn.hi, sn.en) || sn.en || sn.hi}</span>
                      {(pickC(sn.note_hi, sn.note_en) || sn.note) && <span className="mt-0.5 block text-sm text-ink-2 pf-special-note">{pickC(sn.note_hi, sn.note_en) || sn.note}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {panchang_sections.length > 0 && (
        <section className="mx-auto max-w-5xl px-5 pb-16 md:px-8" data-testid="panchang-custom-sections">
          <div className="space-y-10 border-t border-line pt-12">
            {panchang_sections.map((s, i) => (
              <div key={i} data-testid={`panchang-custom-${i}`}>
                <h2 className={`text-2xl text-ink deva-lh pf-section-title ${paLang === "hi" ? "font-deva" : "font-serif-latin"}`}>{pickC(s.title_hi, s.title_en) || s.title_en || s.title_hi}</h2>
                <p className="mt-3 max-w-3xl whitespace-pre-line leading-relaxed text-ink-2 pf-section-body">{pickC(s.body_hi, s.body_en) || s.body_en || s.body_hi}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Legacy settings-driven glossary removed — PanchangExtras renders the single
          searchable Panchangam glossary (document-sourced). */}
      <div className="mx-auto max-w-5xl px-5 pb-16 md:px-8">
        <CivilTithiNote className="mb-8" />
        <JourneyStrip links={["calendar", "rituals", "dainik", "mantras", "festivals"]} />
      </div>
      <PanchangExtras />
      <PremiumBanner className="py-12" />
      <PanchangPremiumModal />
    </div>
    </GlossaryProvider>
  );
}

const GLOSSARY = [
  ["Tithi", "तिथि", "A lunar day — the time for the Moon to move 12° from the Sun. There are 30 tithis in a lunar month, and each shapes which activities are favourable.", "चन्द्र दिवस — सूर्य से चन्द्रमा के 12° अंतर का काल। एक चन्द्र मास में 30 तिथियाँ होती हैं।"],
  ["Nakshatra", "नक्षत्र", "One of 27 lunar mansions the Moon travels through. Your birth nakshatra guides naming, muhurta and compatibility.", "27 चन्द्र-भवनों में से एक जिनसे चन्द्रमा गुज़रता है। जन्म-नक्षत्र नामकरण एवं मुहूर्त में सहायक है।"],
  ["Yoga", "योग", "A combination of the Sun's and Moon's longitudes (27 yogas). Each lends a particular quality to the day.", "सूर्य एवं चन्द्रमा के देशांतर का योग (27 योग)। प्रत्येक दिन को एक विशेष गुण देता है।"],
  ["Karana", "करण", "Half a tithi (11 karanas repeating). Karanas help fine-tune the timing of tasks within a day.", "आधी तिथि (11 करण)। दिन के भीतर कार्यों के समय-निर्धारण में सहायक।"],
  ["Vara", "वार", "The weekday, each ruled by a planet (e.g. Ravivara/Sunday by the Sun) that colours the day's energy.", "सप्ताह का दिन, प्रत्येक एक ग्रह द्वारा शासित (जैसे रविवार — सूर्य)।"],
  ["Hora", "होरा", "The planetary hour — day and night are split into hours each ruled by a planet, ideal for timing specific work.", "ग्रह-घण्टा — दिन-रात को ग्रह-शासित घण्टों में बाँटा जाता है, विशेष कार्यों हेतु उपयुक्त।"],
  ["Muhurta", "मुहूर्त", "An auspicious window for an activity. Brahma & Abhijit are favourable; Rahu, Yamaganda & Gulika are best avoided.", "किसी कार्य हेतु शुभ काल। ब्रह्म एवं अभिजित शुभ; राहु, यमगण्ड एवं गुलिक त्याज्य।"],
  ["Choghadiya", "चौघड़िया", "Eight day and eight night segments rating each span as good, neutral or to be avoided — a quick daily guide.", "दिन एवं रात के आठ-आठ खण्ड, प्रत्येक को शुभ/सामान्य/अशुभ बताते हुए।"],
  ["Paksha", "पक्ष", "The fortnight — Shukla (waxing, brightening Moon) and Krishna (waning, dimming Moon).", "पखवाड़ा — शुक्ल (बढ़ता चन्द्र) एवं कृष्ण (घटता चन्द्र)।"],
  ["Ayanamsa", "अयनांश", "The angle between the sidereal and tropical zodiacs (Lahiri). Dharmartha uses the sidereal (nirayana) system.", "निरयन एवं सायन राशिचक्र के बीच का कोण (लाहिरी)। धर्मार्थ निरयन प्रणाली प्रयोग करता है।"],
];

function PanchangamGlossary({ pickC }) {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-20 md:px-8" data-testid="panchangam-glossary">
      <div className="border-t border-line pt-10">
        <p className="overline-label mb-2 text-gold">{pickC("शब्दावली", "Glossary")}</p>
        <h2 className="font-serif-latin text-2xl text-ink">{pickC("पंचांगम् शब्दावली", "Panchangam Glossary")}</h2>
        <p className="mt-2 max-w-2xl text-sm text-ink-2">{pickC("पंचांगम् के पाँच अंग एवं मुख्य काल-गणना शब्दों की सरल व्याख्या।", "A plain-language guide to the five limbs of the Panchangam and the key timing terms.")}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {GLOSSARY.map(([en, hi, den, dhi]) => (
            <div key={en} className="pan-card p-5" data-testid={`glossary-${en.toLowerCase()}`}>
              <p className="font-serif-latin text-lg text-ink">{pickC(hi, en)} <span className="ml-1 text-sm text-ink-3">{pickC(en, hi)}</span></p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{pickC(dhi, den)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
